/**
 * DetectPage — port of v1 index.js DetectPage (research/01 §1.3; REQ-BOOT-5/6).
 * Order: the pageurl lookup is still the one that's AWAITED first and still
 * takes precedence (truthy → ProcessNonProductPage, not awaited); DIV-011 only
 * adds a non-awaited productid PREFETCH issued a moment earlier so the two
 * round-trips overlap. Else CART last-path-segment rule; else the product branch
 * with the one-page-load-stale renderingDelayInMs, the
 * 'cpc-rendering-delay-configured' log, and the IsCPCScriptLoaded/preview dedup
 * guard inside the setTimeout.
 *
 * DIV-022: a REJECTED pageurl lookup is no longer fatal — it is read as "no
 * non-product mapping" and the page carries on, exactly as it does for a 204.
 */
import { appState } from "../state/app-state";
import { getNonProductByPageUrl } from "../api/calculator-by-pageurl";
import { prefetchCalculatorByProduct } from "../api/calculator-by-product";
import { prefetchResolve } from "../api/calculator-resolve";
import { ProcessCartPage } from "../cart";
import { ProcessProductPage, prefetchProductMetaData } from "./product-page";
import { ProcessNonProductPage } from "./nonproduct-page";
import { requestStyles } from "./styles";
import { startClientPerformanceAttempt } from "../performance/client-performance-lifecycle";

//This method detects the page and runs the required script
export async function DetectPage(): Promise<void> {
  // Telemetry: initialization really begins here, on every page the bundle runs
  // on. Cart pages and the dedupe-skipped product branch below never COMPLETE
  // the attempt, so neither of them ever sends a report.
  startClientPerformanceAttempt();
  const path = window.location.pathname;
  const pageUrl = window.location.href;

  // DIV-027: ONE request for the page's calculator — pageurl mapping, product
  // assignment and the config payload together. The lookups below, and the
  // config calls in the page processors, read from it and only hit the network
  // themselves when it is unavailable. Everything after this line is unchanged.
  prefetchResolve();

  // DIV-011 (Crisp ses_1c6b4bd2, Foilman — 6-8s widget load): kick the product
  // lookup off HERE, in parallel with the pageurl lookup below, instead of
  // waiting for the pageurl response and only then firing it from
  // ProcessProductPage. On a product page the pageurl lookup is (all but) always
  // a 204, so back-to-back it was a whole round-trip of dead wait. Guarded to
  // the case ProcessProductPage would actually run: skip it on the cart page
  // (some themes still expose window.meta.product there) and — inside
  // prefetchCalculatorByProduct — when there is no product id at all. A mapped
  // non-product page CAN'T be known until pageurl returns, so on the rare one
  // that also carries a product id the prefetched response is just discarded.
  if (path.substring(path.lastIndexOf("/") + 1).toUpperCase() !== "CART") {
    prefetchCalculatorByProduct();
    // DIV-030: the product's own .js (variants) in flight from the start, too.
    prefetchProductMetaData();
    // DIV-034: and the two stylesheets — see bootstrap/styles.ts for why here
    // and not (any longer) when the calculator id is known.
    if (appState.cpc_productId > 0) requestStyles();
  }

  var calcNonProducts;
  try {
    calcNonProducts = await getNonProductByPageUrl(pageUrl);
  } catch {
    // DIV-022: a pageurl lookup that never completed means exactly one thing — we
    // do not know of a non-product mapping for this page. It is NOT a reason to
    // stop. So it is treated like the 204 it almost always would have been, and
    // the page carries on down the normal branches below.
    //
    // This used to report `configuration_request_failed` and rethrow, which killed
    // the whole bootstrap (the BUG-010 "uncaught rejection is the error UI"
    // parity). The cost of that, measured in production 2026-09-15..17:
    //   * the first request a page makes was rejected on 1–5% of page views
    //     (shoppers leaving mid-request, content blockers, mobile drops);
    //   * 95% of every reported configuration failure was THIS lookup
    //     (`TypeError:pageurl_lookup` 75 vs `calculator_app` 4);
    //   * and on a product page the lookup is all but always a 204 that decides
    //     nothing — yet its failure threw away a product lookup that DIV-011 had
    //     already put in flight, so a shopper lost a calculator they would
    //     otherwise have seen, over a request that did not matter.
    //
    // Nothing is reported here. What the page view really was is decided by what
    // happens next, which is what the telemetry is for: the calculator renders
    // (success), there is none for this page (not applicable, silent), or the
    // lookups that DO matter fail even after their retry (a real failure).
    //
    // The one case this cannot rescue is a genuinely mapped non-product page
    // whose lookup fails: with no mapping known there is nothing to render, as
    // before — only now without an unhandled rejection in the console.
    calcNonProducts = null;
  }
  if (!!calcNonProducts) ProcessNonProductPage(calcNonProducts);
  else {
    const potentialHandle = path.substring(path.lastIndexOf("/") + 1);
    switch (potentialHandle.toUpperCase()) {
      case "CART":
        ProcessCartPage();
        break;

      default:
        var renderingDelayInMs = appState.cpc_configData.renderingDelayInMs || 0;
        console.log("cpc-rendering-delay-configured", renderingDelayInMs);
        setTimeout(function () {
          //Only load script if its not already loaded. This will prevent the issue with
          let shouldLoadCalculator = window.IsCPCScriptLoaded === undefined || appState.cpc_isPreview === true;

          if (shouldLoadCalculator) {
            window.IsCPCScriptLoaded = true;
            ProcessProductPage();
          }
        }, renderingDelayInMs);
        break;
    }
  }
}
